import React, { useEffect, useState } from 'react';
import {
    Box, Button, Grid, lighten, useTheme
} from '@mui/material';
import { useFormik, Form, FormikProvider } from 'formik';
import { OrganizationMemberModel } from '../../typings';
import { OrganizationMemberSchema } from '../../validationSchemes';
import { SelectInput } from '@lp/lp-ui';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { useCurrentUser } from '../../app/useCurrentUser';
import useCache from '../../app/useCache';
import { CachedEntity } from '../../app/cacheSlice';
import { toRoleModels } from '../../functions';
import { userDataSelected } from '../../app/userSlice';
import RoleSelector from '../common/RoleSelector';

type OrganizationPanelProps = {
    data: OrganizationMemberModel,
    handleClose: () => void
}

const OrganizationPanel: React.FC<OrganizationPanelProps> = ({ data, handleClose }: OrganizationPanelProps) => {
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const userState = useAppSelector(store => store.user);
    const currentUser = useAppSelector(store => store.currentUser);
    const roles = useCache(CachedEntity.Role);
    const isNew = data.organizationId === 0;
    const [ isAdmin, isTenantAdmin,] = useCurrentUser();
    const [organizationSelected, setOrganizationSelected] = useState<number>(data.organizationId);

    useEffect(() => {
        setOrganizationSelected(data.organizationId);
    }, [data]);

    const formik = useFormik<OrganizationMemberModel>({
        initialValues: data,
        enableReinitialize: true,
        validationSchema: OrganizationMemberSchema,
        onSubmit: async (values) => {
            await handleSubmit(values);
        }
    });

    const {
        errors,
        touched,
        values,
        getFieldProps,
        submitForm
    } = formik;

    const onOrganizationsChanged = (orgs: OrganizationMemberModel[]) => {
        dispatch(userDataSelected({ ...userState.data!, userOrganizations: orgs }));
    }

    const handleSubmit = async (organizationMember: OrganizationMemberModel) => {
        const organizations = data ? userState.data?.userOrganizations ?? [] : [];
        const index = organizations.findIndex(o => o.organizationId === organizationSelected && organizationSelected !== 0);
        if (index > -1) {
            const clone = [...organizations];
            clone[index] = organizationMember;
            onOrganizationsChanged(clone);
        } else {
            onOrganizationsChanged([...organizations, organizationMember]);
        }
        formik.resetForm();
        handleClose();
    };

    const onOrganizationSelected = (event: React.ChangeEvent<any>) => {
        formik.handleChange(event);
        const organizationId = event.target.value;
        setOrganizationSelected(organizationId);
        const organization = currentUser.data?.organizations?.find(o => o.id === organizationId);
        formik.setFieldValue("organizationName", organization ? organization.name : '');
        formik.setFieldValue("organizationDisplayName", organization ? organization.displayName : '');
    }

    const handleRolesChange = (ids: number[]) => {
        const nextRoles = toRoleModels(ids, roles, values.roles);
        formik.setFieldValue("roles", nextRoles);
        //For non-tenant admin reflect changes in state immediately
        if (!isTenantAdmin) {
            onOrganizationsChanged([{ ...values, roles: nextRoles }]);
        }
    }

    return (
        <React.Fragment>
            <FormikProvider value={formik}>
                <Form autoComplete="off" noValidate onSubmit={submitForm}>
                    <Box py={3} px={5} sx={{ background: lighten(theme.palette.primary.light, 0.5) }}>
                        <Grid container spacing={0} columnSpacing={6}>
                            <Grid item xs={12}>
                                <SelectInput
                                    label="Organization"
                                    isEditing={isNew && isAdmin}
                                    required
                                    {...getFieldProps("organizationId")}
                                    options={[
                                            { value: 0, label: '-' }, // blank option
                                            ...(currentUser.data?.organizations
                                                ? currentUser.data.organizations.filter(o => !(userState.data?.userOrganizations ?? []).some(uo => uo.organizationId === o.id) || o.id === organizationSelected)
                                                                .map(o => ({ value: o.id, label: o.displayName }))
                                                : [])
                                        ]}
                                    onChange={onOrganizationSelected}
                                    error={Boolean(touched.organizationId && errors.organizationId)}
                                    helperText={touched.organizationId && errors.organizationId}
                                    value={organizationSelected}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <RoleSelector
                                    roles={roles}
                                    selectedIds={values.roles.map(r => r.id)}
                                    onChange={handleRolesChange}
                                    disabled={!isAdmin}
                                    height={380}
                                />
                            </Grid>
                            {isTenantAdmin &&
                                <Grid item xs={12}>
                                    <Box display="flex">
                                        <Box flexGrow={1} />
                                        <Button onClick={() => { formik.resetForm(); handleClose(); }}>Cancel</Button>
                                        <Button onClick={submitForm}>Ok</Button>
                                    </Box>
                                </Grid>
                            }
                        </Grid>
                    </Box>
                </Form>
            </FormikProvider>
        </React.Fragment>
    );
}

export default OrganizationPanel;
